<template>
  <div class="container">
    <Breadcrumb :items="breadcrumbData" :short="false"></Breadcrumb>
    <ModalPLPFilters
      :filters="filters.available"
      :categories="mappedCategory"
      :open-modal="filterModal"
      @close="
        () => {
          filterModal = false
        }
      "
    />
    <div class="row">
      <div class="list col-12">
        <div class="header">
          <h1 v-if="categoryTitle" v-text="categoryTitle"></h1>
          <h1 v-else>
            {{ $t('Our collection') }}
          </h1>
          <div v-html="categoryDescription" v-if="categoryDescription"></div>
          <p v-else>
            {{
              $t(
                'Passing on ancestral skills in order to forge the future. Fulfilling the destiny of a Fine Watchmaking Swiss Manufacturer consistently driven by daring, authenticity and passion since 1865.'
              )
            }}
          </p>
        </div>
        <CollectionFilter :categories="mappedCategory" />
        <div
          ref="filter"
          class="filters"
          :class="{ '-active': activeFilter }"
          v-bsl="activeFilter"
        >
          <button class="link-light filters-button"
                  type="button"
                  :aria-label="$t('Open filters')"
                  aria-controls="modal-availability"
                  :aria-expanded="filterModal ? 'true' : 'false'"
                  @click="filterModal = true"
          >
            <FilterIcon />
            <span class="filters-button__text">
              {{ $t('Filters') }}
              <span v-if="Object.keys(activeFilters).length > 0">({{ Object.keys(activeFilters).length }})</span>
            </span>
          </button>
          <transition-group name="fade" tag="div">
            <template v-if="hasActiveFilters">
              <transition-group
                name="fade"
                tag="ul"
                class="filters-active"
                key="filters-active"
              >
                <li v-for="filter in activeFilters" :key="filter.attribute_code + '-' + filter.id">
                  <button
                    type="button"
                    class="btn filters-active__button -tiny"
                    @click="
                      resetFilter(filter.attribute_code, filter.id, filter.label)
                    "
                    :aria-label="
                      $t('Remove filter') +
                        ' ' +
                        (filter.label === 'No' || filter.label === 'Yes'
                          ? labels[filter.attribute_code].translation
                          : filter.label)
                    "
                  >
                    <span v-text="
                      filter.label === 'No' || filter.label === 'Yes'
                        ? labels[filter.attribute_code].translation
                        : filter.label
                    "
                    ></span>
                    <i class="icon-close"></i>
                  </button>
                </li>
              </transition-group>
            </template>
          </transition-group>
        </div>
        <search v-if="isStraps" />

        <transition name="fade">
          <ul class="content" v-if="tiles.length && loadedFilter">
            <template v-for="(tile, i) in tiles">
              <!-- Tile could be an editorial block or a product -->
              <!-- eePromo bloc -->
              <PlpEditorialBlock v-if="tile.visual" :key="'editorial-' + i" :i="i" :block="tile" />
              <!-- End eePromo bloc -->
              <!-- Straps Editorial Block -->
              <PlpStrapsEditorialBlock v-else-if="tile.row && tile.product" :key="'strap-block-' + i" :i="i" :block="tile" />
              <!-- End Straps Editorial Block -->
              <!-- Simple Product or Strap item -->
              <li v-else class="list-item" :key="tile.url_key">
                <product-item
                  v-if="!tile.strap_collection"
                  :product="tile"
                  :class-obs="'delay-' + (i % 4)"
                  :list-position="i + 1"
                  :list-name="categoryName"
                  list-type="Filter"
                  in-p-l-p="true"
                  @enterInViewport="onScroll"
                ></product-item>
                <strap-item
                  v-else
                  :product="tile"
                  :class-obs="'delay-' + (i % 4)"
                  :list-position="i + 1"
                  :list-name="categoryName"
                  list-type="Filter"
                  in-p-l-p="true"
                  @enterInViewport="onScroll"
                ></strap-item>
              </li>
            </template>
          </ul>
        </transition>
        <div class="h5 -upper text-center" v-if="!products.length" role="alert">
          {{ $t('No results') }}
        </div>
        <div class="a11y-sr-only" v-else role="alert">
          {{ $t('Search results updated') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GenericSelector from '@vue-storefront/core/compatibility/components/GenericSelector'
import ProductItem from '../components/ProductItem'
import StrapItem from '../components/StrapItem'
import Breadcrumb from '../components/Breadcrumb'
import Category from '@vue-storefront/core/pages/Category'
import { isServer } from '@vue-storefront/core/helpers'
import pickBy from 'lodash-es/pickBy'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { mapGetters } from 'vuex'
import config from 'config'
import { metaLinkBuilder, titleMetaTagBuilder, descriptionMetaTagBuilder, getProductPrice } from '../utilities'
import Search from '../components/plp/Search.vue'
import { ProductListingPageComponent } from 'src/modules/prismic/components/ProductListingPage'
import PlpEditorialBlock from '../components/PlpEditorialBlock.vue'
import PlpStrapsEditorialBlock from '../components/PlpStrapsEditorialBlock.vue'
import CollectionFilter from '../components/CollectionFilter.vue'
import { PAGES } from 'src/modules/prismic/content-types'
import ModalPLPFilters from '../components/ModalPLPFilters.vue'
import FilterIcon from '../components/utils/FilterIcon.vue'

const ITEM_PER_PAGE = 16

export default {
  name: 'PLP',
  mixins: [Category, GenericSelector, ProductListingPageComponent],
  components: {
    ProductItem,
    StrapItem,
    Breadcrumb,
    FilterIcon,
    Search,
    CollectionFilter,
    ModalPLPFilters,
    PlpEditorialBlock,
    PlpStrapsEditorialBlock
  },
  data() {
    return {
      offset: 1,
      dispatchedIntervals: [],
      loadedFilter: true,
      activeFilter: false,
      block: null,
      currentVideo: null,
      launchVideo: false,
      filterModal: false,
      labels: {
        bracelet: { translation: this.$t('Bracelet') },
        diameter: { translation: this.$t('Diameter') },
        movement: { translation: this.$t('Movement') },
        material: { translation: this.$t('Material') },
        new_reference: { translation: this.$t('New reference') },
        collection: { translation: this.$t('Collection') },
        limited_edition: { translation: this.$t('Limited edition') },
        is_date: { translation: this.$t('Date') },
        is_moonphase: { translation: this.$t('Moonphase') },
        is_chronograph: { translation: this.$t('Chronograph') },
        is_double_tourbillon: { translation: this.$t('Double tourbillon') },
        is_tenth: { translation: this.$t('Tenth') },
        is_hundredth: { translation: this.$t('Hundredth') },
        is_tachymetric: { translation: this.$t('Tachymetric') },
        is_shoppable: { translation: this.$t('Shoppable') },
        is_skeleton: { translation: this.$t('Skeleton') },
        is_110th_second_chronograph: { translation: this.$t('110th second chronograph') },
        is_small_second: { translation: this.$t('Small second') },
        is_triple_calendar: { translation: this.$t('Triple calendar') },
        is_tourbillon: { translation: this.$t('Tourbillon') },
        is_instantaneous_big_date: { translation: this.$t('Instantaneous big date') },
        is_fusee_chainee: { translation: this.$t('Fusee chainee') },
        is_gyroscopic_escapement: { translation: this.$t('Gyroscopic escapement') },
        is_central_second: { translation: this.$t('Central second') },
        is_110th_of_a_second_indicator: { translation: this.$t('110th of a second indicator') },
        is_flyback: { translation: this.$t('Flyback') },
        is_1100th_second_chronograph: { translation: this.$t('1100th second chronograph') },
        gender: { translation: this.$t('Gender') },
        strap_color: { translation: this.$t('Color') },
        strap_material: { translation: this.$t('Material') },
        strap_size: { translation: this.$t('Size') },
        strap_collection: { translation: this.$t('Collection') },
        interhorn_case: { translation: this.$t('Interhorn Case') },
        interhorn_buckle: { translation: this.$t('Interhorn Buckle') }
      }
    }
  },
  preAsyncData({ store, route }) {
    if (route.query.page) {
      store.dispatch('category/mergeSearchOptions', {
        perPage: ITEM_PER_PAGE,
        sort: 'position:asc',
        current: +route.query.page * ITEM_PER_PAGE
      })
      // this.pagination.current = +route.query.page * 16
      // this.pagination.perPage = 16
    } else {
      store.dispatch('category/mergeSearchOptions', {
        sort: 'position:asc',
        perPage: ITEM_PER_PAGE
      })
    }
  },
  async asyncData({ store, route, context }) {
    if (route.query.page) {
      await store.dispatch('category/mergeSearchOptions', {
        perPage: ITEM_PER_PAGE,
        sort: 'position:asc',
        current: (+route.query.page - 1) * ITEM_PER_PAGE
      })
    } else {
      await store.dispatch('category/mergeSearchOptions', {
        perPage: ITEM_PER_PAGE,
        sort: 'position:asc'
      })
    }
    return new Promise((resolve, reject) => {
      if (context) context.output.cacheTags.add(`products`)
      return resolve()
    })
  },
  created() {
    this.pagination.current =
      +this.$route.query.page >= 1
        ? (+this.$route.query.page - 1) * ITEM_PER_PAGE
        : 0
    this.pagination.perPage = ITEM_PER_PAGE
  },
  beforeMount() {
    this.$bus.$on('filter-changed-category', this.onFilter)
    this.$bus.$on('filter-changed-category', this.buildUrl)
    this.$bus.$on('filter-reset', this.onFilter)
  },
  mounted() {
    setTimeout(() => {
      this.setFiltersFromUrl()
    }, 1000)
  },
  methods: {
    getProductPrice: getProductPrice,
    buildUrl() {
      if (Object.keys(this.filters.chosen).length) {
        history.replaceState({}, '', this.$route.path + this.query)
      } else {
        history.replaceState({}, '', this.$route.path)
      }
    },
    setFiltersFromUrl() {
      const booleanMapping = {
        true: 'Yes',
        false: 'No'
      }
      const filtersFromUrl = this.query
        .replace('?', '')
        .split('&')
        .map(str => str.split('='))
      const filters = Object.entries(this.filters.available)
        .map(([key, value]) => filtersFromUrl.find(array => array[0] === key))
        .filter(entry => entry)
        .map(([key, value]) => {
          const filter = this.filters.available[key].find(
            ({ id }) => id === value
          )
          let label = filter.label
          if (booleanMapping[label]) {
            label = booleanMapping[label]
          }
          return {
            attribute_code: key,
            id: filter.id,
            label
          }
        })
      filters.forEach(({ attribute_code, id, label }) => {
        this.$bus.$emit('filter-changed-category', {
          attribute_code,
          id,
          label
        })
      })
    },
    sortById(filters) {
      return [...filters].sort((a, b) => {
        return a.id - b.id
      })
    },
    // Show / Hide filter modal on mobile
    toggleFilter() {
      if (window.innerWidth < 992) {
        this.activeFilter = !this.activeFilter
        this.dispatchedIntervals = []
      }
    },
    resetFilter(code, id, label) {
      this.$bus.$emit('filter-changed-category', {
        attribute_code: code,
        id: id,
        label: label
      })
      this.dispatchedIntervals = []
    },
    onFilter() {
      this.callGTM('Search', 0, this.products.length)
      this.loadedFilter = false
      setTimeout(() => {
        this.loadedFilter = true
      }, 400)
    },
    onScroll(index) {
      const isTreshold = index - 8 >= 0 && Number.isInteger(index / 8)
      if (isTreshold) {
        const start = index - 8 >= 0 ? index - 8 : 0
        const end = start + 8
        if (!this.dispatchedIntervals.includes(start)) {
          this.callGTM('Category', start, end)
          this.dispatchedIntervals.push(start)
        }
      }
    },
    callGTM(type, start, end) {
      this.$gtm.trackEvent({
        event: 'eeListView',
        listName: this.categoryName,
        listType: 'Filter',
        products: this.products.slice(start, end).map((product, i) => {
          return {
            name: product.name || '',
            id: product.sku || '',
            position: start + i + 1,
            price: getProductPrice(product) || 0,
            brand: 'Zenith',
            category: 'Watches',
            productCase: product.option_value_material || '',
            productCaseSize: product.option_value_diameter || '',
            productColor: product.option_value_dial || '',
            productMasterId: product.collection || '',
            productSubCategory: product.option_value_line,
            productMidCategory: product.option_value_collection || '',
            productPowerReserve: product.option_value_power_reserve || '',
            productStock:
              product.stock && product.stock.is_in_stock
                ? 'In stock'
                : 'Out of stock',
            productStrap: product.option_value_bracelet || '',
            productWaterResistance: product.option_value_water_resistance || ''
          }
        })
      })
    },
    callEditoGTM(event, i) {
      let edito = this.blocks.find(e => e.position === i + 1)
      if (edito) {
        let data = {
          event: event,
          promotion: {
            creative: 'PLP Edito',
            id: 'PLP_edito_' + edito.position,
            name: edito.title,
            position: edito.position
          }
        }
        this.$gtm.trackEvent(data)
        if (edito.ctaLink) {
          data.nextPageVisited = edito.ctaLink
        }
      }
    },
    findBlock() {
      let r = this.blocks.find(e => e.position === i + 1)
      return r
    }
  },
  computed: {
    ...mapGetters('category', [
      'getCurrentCategory',
      'getActiveCategoryFilters',
      'getCurrentCategoryProductQuery'
    ]),
    breadcrumbData() {
      switch (this.category.parents_count - 1) {
        case 0:
          return [{ name: 'Homepage', path: '/' }, { name: this.categoryName }]
        case 1:
          return [
            {
              name: this.category.parents_data[0].name,
              url_key: this.category.parent_id
            },
            { name: this.categoryName }
          ]
        case 2:
          return [
            {
              name: this.category.parents_data[1].name,
              url_key: this.category.parent_id
            },
            { name: this.categoryName }
          ]
        default:
          return []
      }
    },
    mappedCategory() {
      const mapChildren = children =>
        children
          .filter(child => child.active_product_count >= 1)
          .filter(child => child.include_in_menu)
          .map(child => ({
            name: child.name,
            url_key: child.url_key,
            position: child.position,
            children: child.children_data
              ? mapChildren(child.children_data)
              : []
          }))
      return {
        name: this.category.name,
        url_key: this.category.url_key,
        children: mapChildren(this.category.children_data || [])
      }
    },
    localizedUrl() {
      const { url } = currentStoreView()
      return url || ''
    },
    seoAttributes() {
      return Object.values(this.filters.chosen)
        .map(({ attribute_code, label }) => ({
          [attribute_code]: label
        }))
        .reduce((acc, curr) => ({ ...acc, ...curr }), {})
    },
    query() {
      if (Object.keys(this.filters.chosen).length) {
        return Object.values(this.filters.chosen).reduce(
          (acc, { attribute_code, id }) => {
            acc += acc.length ? '&' : '?'
            acc += `${attribute_code}=${id}`
            return acc
          },
          ''
        )
      }
      return Object.entries(this.$route.query).reduce((acc, [key, value]) => {
        acc += acc.length ? '&' : '?'
        acc += `${key}=${value}`
        return acc
      }, '')
    },
    showPagination() {
      return isServer
    },
    clientUrl() {
      return config.client.url
    },
    category() {
      return this.getCurrentCategory
    },
    isStraps() {
      return this.category.slug === 'straps'
    },
    activeFilters() {
      return this.getActiveCategoryFilters
    },
    availableFilters() {
      return pickBy(this.filters, filter => filter.length)
    },
    hasActiveFilters() {
      return Object.keys(this.activeFilters).length !== 0
    },
    products() {
      return this.$store.state.product.list.items.map((item, i) => {
        let obj = Object.assign({}, item)
        obj.imageHover = item.secondary_image
        obj.swapImages = (i % 3 === 0) // Show secondary image at indices 0, 3, 6, 9, etc.
        return obj
      })
    },

    displayableBlocks() {
      const blockRows = this.blocks.map(block => block.row).sort((a, b) => a - b)
      const blockRowsUnique = [...new Set(blockRows)]
      const tilePerRow = this.$store.state['zenith-ui'].menu.mobile ? 2 : 5
      return this.blocks.filter(block => (block.row - 1) <= (Math.ceil(this.products.length / tilePerRow) + blockRowsUnique.indexOf(block.row)))
    },
    displayableStrapsBlocks() {
      const blockRows = this.strapsBlocks.map(block => block.row).sort((a, b) => a - b)
      const blockRowsUnique = [...new Set(blockRows)]
      const tilePerRow = this.$store.state['zenith-ui'].menu.mobile ? 2 : 5
      return this.strapsBlocks.filter(block => (block.row - 1) <= (Math.ceil(this.products.length / tilePerRow) + blockRowsUnique.indexOf(block.row)))
    },
    tiles() {
      return [...this.displayableBlocks, ...this.displayableStrapsBlocks, ...this.products]
    },
    categoryTitle() {
      return this.category.title
    },
    categoryDescription() {
      return this.category.description
    },
    blocks() {
      return this.plpGridEditorialComponents &&
        this.plpGridEditorialComponents.length
        ? this.plpGridEditorialComponents
        : []
    },
    strapsBlocks() {
      return this.plpStrapsEditorialComponents &&
        this.plpStrapsEditorialComponents.length
        ? this.plpStrapsEditorialComponents
        : []
    },
    currentCountry() {
      return currentStoreView().i18n.defaultCountry
    },
    pageName() {
      return this.category.parent_id === 'root' ? PAGES.CLP : PAGES.PLP
    },
    builderActiveFilters() {
      const activeFilterKeys = Object.keys(this.activeFilters)
      const filters = activeFilterKeys.reduce((text, filterKey) => {
        const filterName = this.labels[filterKey].translation
        const filterValue = this.$t(this.activeFilters[filterKey].label)
        return `${text ? `${text}, ` : ''}${filterName}: ${filterValue}`
      }, '')
      return filters
    },
    builderItemsCategory() {
      return this.$t(this.category.parents_data[0].name)
    },
    builderFiltersText() {
      return this.builderActiveFilters ? ` - ${this.builderActiveFilters}` : ''
    }
  },
  metaInfo() {
    const currentPage = this.pagination.current / ITEM_PER_PAGE + 1

    const titleBuilderData = {
      collection: this.category.name,
      itemsCategory: this.builderItemsCategory,
      filtersText: this.builderFiltersText,
      market: this.currentCountry
    }
    const descriptionBuilderData = {
      collection: this.category.name,
      itemsCategory: this.builderItemsCategory,
      filters: this.builderActiveFilters,
      filtersText: this.builderFiltersText
    }

    return {
      link: [
        ...metaLinkBuilder(this.$route).link
      ],
      title: titleMetaTagBuilder.call(this, this.pageName, titleBuilderData),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: descriptionMetaTagBuilder.call(this, this.pageName, descriptionBuilderData)
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';
// Fade
.fade-enter,
.fade-leave-to {
  position: static;
}

// Fade
.showPlyr-enter-active,
.showPlyr-leave-active {
  transition: opacity 0.4s ease-in-out;
}

.showPlyr-enter,
.showPlyr-leave-to {
  opacity: 0;
  position: absolute;
}

.h5 {
  color: var(--zenith-theme-primary);
}

.container {
  max-width: 1640px;
}

.breadcrumb {
  max-width: 100%;
  position: relative;
  top: unset;
  left: unset;
}

.header {
  margin: 9rem auto 4rem;
  color: var(--zenith-theme-primary);
  max-width: 55%;
  text-align: center;

  @include bp(max lg) {
    max-width: none;
  }

  p {
    color: var(--zenith-component-text);
  }
}

.filters {
  display: flex;
  align-items: center;
  margin-block: 3rem;
  padding-left: 1rem;
  flex-wrap: wrap;
  gap: 3rem;

  &-button {
    margin-right: 0;
    font-weight: 300;
  }

  &-active {
    list-style: none;
    padding-left: 0;
    display: flex;
    align-items: center;
    margin-bottom: 0;
    flex-wrap: wrap;
    gap: 1rem;

    &__button:not(.-stroke) {
      display: flex;
      align-items: center;
      flex-direction: row;
      font-size: 1rem;
      padding: 1.2rem 0.8rem;
      gap: 0.7rem;
      color: var(--zenith-plp-filter-active-color);

      span {
        flex: 1;
      }

      .icon-close {
        background-color: var(--zenith-white);
        border-radius: 50%;
        padding: 0.3rem;
      }

      &::after {
        border: 0.1rem solid var(--zenith-plp-filter-active-background);
        border-color: transparent;
        background-image: linear-gradient(-45deg, var(--zenith-plp-filter-active-background), var(--zenith-plp-filter-active-background), var(--zenith-btn-background-gradient-hover), var(--zenith-btn-background-gradient-hover));
      }

      &:hover::after {
        border-color: var(--zenith-plp-filter-active-background);
      }
    }
  }
}

.content {
  margin: 0;
  list-style: none;
  position: relative;
  display: grid;
  padding-left: 0;
  column-gap: 2.5rem;
  grid-template-columns: repeat(5,minmax(0, 1fr));

  @include bp(max md) {
    column-gap: .5rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .list-item {
    position: relative;
    margin-bottom: 3rem;

    /deep/ .obs {
      height: 100%;
    }

    /deep/ > .obs {
      transform: translateY(0);

      &.obs-fade-up {
        transform: translateY(10rem);
      }
    }
  }
}
</style>
